import React, { useState, useEffect, useCallback, useRef } from "react";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useMessage } from "../../context/MessageContext";
import { getApi } from "../../apis/api";
import { GENERAL_ERROR_MESSAGE } from "../../utils/message";
import { Loader } from "../../components/loaders/loaders";

interface MultiSelectChipsProps {
  dropdownData: any[]; // Data to display in the dropdown
  setDropdownData: (data: any) => void; // Function to update dropdown data
  selectedData: any[]; // The currently selected chips (items)
  setSelectedData: (data: any) => void; // Function to update selected chips
  apiUrl: string; // API endpoint for searching dropdown dataes
  limit?: number; // Items per page for pagination
  labelTitle?: string; // Optional label for the input
  mandatory?: boolean; // Whether the input is mandatory
  searchPlaceholder?: string; // Placeholder text for search input
  disable?: boolean; // Disbale the dropdown
  keyBindName: string; // Mapping data by key name
  limitSelection?: number; // Maximum number of selections allowed
  parentCustomerId?: string; // parentCustomerId for get customers of parent customer
  includeAllOption?: boolean; // include the "All" option
  disableAllOption?: boolean; // Disable the "All" option
  skipApiCallWhenNoParent?: boolean; // control API call based on parentCustomerId
  extendedUrl?: Record<string, string>; // Additional key-value pairs to append to the API URL
  extraParentId?: {
    key: string;
    id: string;
  };
  disabledAll?: boolean;

  /** NEW (optional): identify usage context */
  callingFrom?: string; // e.g. "TraditionalQuote"
  onApply?: () => void; // Callback when Enter is pressed with no highlighted item
  /** When true, opening the dropdown won't refetch if data is already loaded
   *  (still fetches on open when the list is empty). Default false = refetch on open. */
  skipOpenRefetchIfLoaded?: boolean;
  /**
   * Identifies this control to the mandatory-field validation, which locates fields by
   * `data-field` and focuses the first one that is missing a value.
   */
  fieldKey?: string;
  /** Validation message rendered beneath the control. */
  error?: string;
}

/**
 * MultiSelectChips Component: A reusable multi-select dropdown with chips functionality.
 */
const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
  dropdownData,
  setDropdownData,
  selectedData,
  setSelectedData,
  apiUrl,
  limit = 15,
  labelTitle = "Select",
  mandatory,
  searchPlaceholder = "Search",
  disable,
  keyBindName,
  limitSelection = Infinity,
  parentCustomerId,
  includeAllOption,
  disableAllOption,
  skipApiCallWhenNoParent,
  extendedUrl,
  extraParentId,
  disabledAll = false,
  callingFrom, // NEW
  onApply,
  skipOpenRefetchIfLoaded = false,
  fieldKey,
  error,
}) => {
  const { setMessage } = useMessage();
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const isFetching = useRef(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  /** NEW: predicate + factory for the 3 extra Delivery accessorials */
  const shouldInjectExtras = (): boolean => {
    const isLtlAccessorials =
      typeof apiUrl === "string" &&
      apiUrl.toLowerCase().includes("/ltl-accessorial");
    const isDelivery = String(extendedUrl?.isPickup ?? "") === "false";
    return callingFrom === "TraditionalQuote" && isLtlAccessorials && isDelivery;
  };


  const getExtraOptions = () => {
    // Stable IDs to avoid collisions with backend IDs
    const base = [
      { id: "custom_pallet_jack", name: "Pallet Jack" },
      { id: "custom_white_glove_no_assembly", name: "White Glove No Assembly" },
      { id: "custom_white_glove_with_assembly", name: "White Glove with Assembly" },
    ];
    if (!keyword.trim()) return base;
    const q = keyword.trim().toLowerCase();
    return base.filter((it) => it.name.toLowerCase().includes(q));
  };

  /**
   * Toggles the dropdown open/close state.
   */
  const toggleDropdown = () => {
    if (!disable) {
      setIsOpened(!isOpened);
    }
  };

  /**
   * Handles selecting or deselecting an item from the dropdown or chips.
   */
  const handleSelectItem = (item: any) => {
    if (selectedData.find((selected) => selected.id === item.id)) {
      // Item is already selected, so remove it (deselect)
      setSelectedData(
        selectedData.filter((selected) => selected.id !== item.id)
      );
    } else {
      // Check if the selected items exceed the allowed limit
      if (selectedData?.length >= limitSelection) {
        // Only allow adding more items if the limit is not reached
        return;
      }
      if (disableAllOption && item.id === "all") {
        return;
      }
      setSelectedData([...selectedData, item]);
    }
  };

  /**
   * Handles removing a selected chip (deselecting).
   */
  const handleRemoveChip = (item: any) => {
    if (!disable) {
      setSelectedData(
        selectedData.filter((selected) => selected.id !== item.id)
      );
    }
  };

  /**
   * Custom hook to handle clicks outside the dropdown, closing it.
   */
  const outsideClickRef = useOutsideClick<HTMLDivElement>(() => {
    setKeyword("");
    setIsOpened(false);
  });

  /**
   * Handles keyboard navigation in the dropdown.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      if (!isOpened || dropdownData.length === 0) return;
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev < dropdownData.length - 1 ? prev + 1 : 0;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      if (!isOpened || dropdownData.length === 0) return;
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev > 0 ? prev - 1 : dropdownData.length - 1;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < dropdownData.length) {
        const item = dropdownData[highlightedIndex];
        if (
          (item.id === "all" && disabledAll) ||
          (selectedData.some((selected) => selected.id === "all") &&
            item.id !== "all") ||
          (selectedData.some((selected) => selected.id !== "all") &&
            item.id === "all")
        )
          return;
        handleSelectItem(item);
        setHighlightedIndex(-1);
      } else if (keyword && dropdownData.length > 0) {
        const exactMatch = dropdownData.find(
          (item: any) => item.name.toLowerCase() === keyword.toLowerCase()
        );
        if (exactMatch) {
          handleSelectItem(exactMatch);
          setKeyword("");
          setHighlightedIndex(-1);
        } else {
          setKeyword("");
          setIsOpened(false);
          onApply?.();
        }
      } else {
        setKeyword("");
        setIsOpened(false);
        onApply?.();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setKeyword("");
      setIsOpened(false);
    }
  };

  const scrollToItem = (index: number) => {
    if (dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll(".dropdown__item");
      if (items[index]) {
        items[index].scrollIntoView({ block: "nearest" });
      }
    }
  };

  /**
   * Handles search input changes and resets pagination.
   */
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setPage(1);
    setHasMore(true);
    if (!isOpened) {
      setIsOpened(true);
    }
  }, [isOpened]);

  /**
   * Converts the extendedUrl object into a query string.
   */
  const getExtendedUrlParams = () => {
    if (!extendedUrl) return "";
    return Object.entries(extendedUrl)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      )
      .join("&");
  };

  /**
   * Fetches filtered data from the API based on the search keyword and other parameters.
   */
  const fetchFilteredData = useCallback(
    async (reset: boolean = false) => {
      // Skip API call if the `skipApiCallWhenNoParent` is true and parentCustomerId is not provided
      if (
        isFetching.current ||
        !apiUrl ||
        isLoading ||
        (skipApiCallWhenNoParent && !parentCustomerId)
      ) {
        return;
      }
      isFetching.current = true; // Prevent multiple calls
      try {
        setIsLoading(true);
        let url = `${apiUrl}?limit=${limit}&page=${reset ? 1 : page}`;
        if (keyword) {
          url += `&keyword=${encodeURIComponent(keyword)}`;
        }
        if (parentCustomerId) {
          url += `&parentCustomerId=${encodeURIComponent(parentCustomerId)}`;
        }
        if (extraParentId?.id) {
          url += `&${encodeURIComponent(extraParentId.key)}=${encodeURIComponent(extraParentId.id)}`;
        }
        // Append the extended URL parameters if they exist
        const extendedUrlParams = getExtendedUrlParams();
        if (extendedUrlParams) {
          url += `&${extendedUrlParams}`;
        }

        const response = await getApi(url);

        if (response.success) {
          const userData: any = response.data || [];
          const mappedData = (Array.isArray(userData) ? userData : []).map((item: any) => {
            return {
              id: item?.id,
              name: item?.[keyBindName],
              customerLevel: item?.customerLevel,
              isCastelleCustomer: item?.isCastelleCustomer ,
            };
          });

          // ✨ NEW: inject extra Delivery accessorials for TraditionalQuote (Delivery side)
          let withExtras = mappedData;
          if (shouldInjectExtras()) {
            const extras = getExtraOptions();
            // avoid duplicates by id
            const existingIds = new Set(mappedData.map((m: any) => m.id));
            const extrasUnique = extras.filter((e) => !existingIds.has(e.id));

            // place extras at the top (after "all" if included)
            if (includeAllOption && (Array.isArray(userData) ? userData?.length > 0 : mappedData?.length > 0)) {
              const allOption = { id: "all", name: "all" };
              withExtras = reset
                ? [allOption, ...extrasUnique, ...mappedData]
                : [...dropdownData, ...extrasUnique, ...mappedData];
            } else {
              withExtras = reset ? [...extrasUnique, ...mappedData] : [...dropdownData, ...extrasUnique, ...mappedData];
            }
          }

          // Original "All" option logic (kept intact if extras not injected)
          if (!shouldInjectExtras()) {
            if (includeAllOption && (Array.isArray(userData) ? userData.length > 0 : mappedData.length > 0)) {
              const allOption = { id: "all", name: "all" };
              setDropdownData(
                reset
                  ? [allOption, ...mappedData]
                  : [...dropdownData, ...mappedData]
              );
            } else {
              if (parentCustomerId) {
                setDropdownData(mappedData);
              } else {
                setDropdownData(
                  reset ? mappedData : [...dropdownData, ...mappedData]
                );
              }
            }
          } else {
            // When extras are injected, we've already built the array including "all" (if needed)
            if (parentCustomerId && reset) {
              // When filtering by parent, keep it replace-style on reset
              setDropdownData(withExtras);
            } else {
              setDropdownData(withExtras);
            }
          }

          setHasMore((Array.isArray(userData) ? userData.length : 0) >= limit);
        } else {
          setMessage(response.error?.message || GENERAL_ERROR_MESSAGE, "error");
        }
      } catch (error) {
        setMessage(GENERAL_ERROR_MESSAGE, "error");
      } finally {
        setIsLoading(false);
        isFetching.current = false; // Reset flag after fetch
      }
    },
    [
      apiUrl,
      keyword,
      page,
      limit,
      setDropdownData,
      dropdownData,
      setMessage,
      isLoading,
      parentCustomerId,
      extraParentId?.id,
      includeAllOption,
      extendedUrl,       // keeping your original dependencies
      callingFrom,       // NEW: re-fetch when this context changes
    ]
  );

  /**
   * Effect to load more data when scrolling reaches the bottom.
   */
  useEffect(() => {
    const handleScroll = () => {
      if (dropdownRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
        if (
          scrollTop + clientHeight >= scrollHeight - 10 &&
          !isLoading &&
          hasMore
        ) {
          setPage((prev) => prev + 1);
        }
      }
    };

    const currentRef = dropdownRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isOpened, isLoading, hasMore]);

  useEffect(() => {
    // Refetch on open by default. When skipOpenRefetchIfLoaded is set, avoid the
    // redundant call if the list already has data (still fetches when empty).
    if (isOpened && (!skipOpenRefetchIfLoaded)) {
      fetchFilteredData(true);
    }
  }, [isOpened]);

  /**
   * Effect to trigger data fetching when the keyword changes, with a debounce mechanism.
   */
  useEffect(() => {
    if (keyword && dropdownData.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [dropdownData, keyword]);

  useEffect(() => {
    if (keyword) {
      const debounceFetch = setTimeout(() => {
        fetchFilteredData(true);
      }, 1000);
      return () => clearTimeout(debounceFetch);
    } else {
      fetchFilteredData(true);
    }
  }, [apiUrl, keyword, callingFrom, extendedUrl?.isPickup]); // include context bits that affect extras

  /**
   * Effect to fetch data when the page number changes.
   */
  useEffect(() => {
    if (page > 1) {
      fetchFilteredData();
    }
  }, [page]);

  /**
   * Fetches data when `parentCustomerId` changes.
   */
  useEffect(() => {
    fetchFilteredData(true);
  }, [parentCustomerId, extraParentId?.id]);

  /**
   * Marks `isFirstRender` as false after the initial render.
   */
  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  return (
    <>
      <div className="form__Field">
        {labelTitle && (
          <label className="form__label form__label--medium">
            {labelTitle}{" "}
            {mandatory && <span className="mandatory-mark">*</span>}
          </label>
        )}
        <div
          className={`multiSelectChip ${isOpened && "open"} ${disable && "multiSelectChip__disabled"
            } ${error ? "multiSelectChip--error" : ""}`}
          // Mandatory-field validation finds controls by this attribute and focuses
          // the first one that is missing a value.
          data-field={fieldKey}
          ref={outsideClickRef}
          onClick={toggleDropdown}
          style={{
            height: selectedData?.length > 0 ? "auto" : "44px",
          }}
        >
          {/* Selected chips display */}
          {selectedData?.length > 0 && (
            <div className="chips">
              {selectedData.map((item) => (
                <div className="chip__item" key={item.id}>
                  {item.name}
                  <button
                    type="button"
                    className="chip__close"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveChip(item);
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input field for searching */}
          <input
            type="text"
            id="search"
            name="search"
            value={keyword}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            disabled={disable}
          />

          {/* Dropdown list */}
          {isOpened && (
            <div className="dropdown" ref={dropdownRef}>
              {dropdownData.length > 0 ? (
                dropdownData.map((item, index) => (
                  <div
                    key={item.id}
                    className={`dropdown__item ${index === highlightedIndex ? "dropdown__item--highlighted" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        (item.id === "all" && disabledAll) ||
                        // Disable all other options if 'all' is selected
                        (selectedData.some(
                          (selected) => selected.id === "all"
                        ) &&
                          item.id !== "all") ||
                        // Disable 'all' option if any other option is selected
                        (selectedData.some(
                          (selected) => selected.id !== "all"
                        ) &&
                          item.id === "all")
                      )
                        return;
                      handleSelectItem(item);
                    }}
                  >
                    <input
                      disabled={
                        (item.id === "all" && disabledAll) ||
                        (selectedData.some((selected) => selected.id === "all") &&
                          item.id !== "all") ||
                        (selectedData.some((selected) => selected.id !== "all") &&
                          item.id === "all")
                      }
                      type="checkbox"
                      className=""
                      checked={
                        !!selectedData.find(
                          (selected) => selected.id === item.id
                        )
                      }
                      onChange={() => handleSelectItem(item)}
                    />
                    <span>{item?.name}</span>
                  </div>
                ))
              ) : (
                <div className="dropdown__noitem">No Data found.</div>
              )}
              {isLoading && <div className="loading">Loading...</div>}
            </div>
          )}
        </div>
        {error && <span className="form__error">{error}</span>}
      </div>
    </>
  );
};

export default MultiSelectChips;
